// src/components/form/ScaleMetricInput.tsx
import React, { FormEvent } from 'react';
import { Card, Flex, Text, Slider, Button, Box } from '@radix-ui/themes';
import { useMetricFormStore } from '@/state/useMetricFormStore';
import { scale1To5Schema } from '@/state/metricTypeValidation';

import { db } from '@/database/client';
import { MetricInputProps } from '@/components/Form/types';
import { CheckCircledIcon } from '@radix-ui/react-icons';

export function ScaleMetricInput({
  definition,
  onSaveSuccess,
}: MetricInputProps) {
  const { id: metricId, name, unit } = definition;

  // Read ephemeral draft from Zustand. Default to midpoint (3) if untouched.
  const rawDraft = useMetricFormStore((s) => s.draftValues[metricId]);
  const draftValue = rawDraft !== undefined ? Number(rawDraft) : 3;
  const error = useMetricFormStore((s) => s.errors[metricId] ?? null);
  const { setDraftValue, setError, clearDraft } = useMetricFormStore();

  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // 1. Execute Zod Validation against our integer scale rules
    const result = scale1To5Schema.safeParse(draftValue);

    if (!result.success) {
      const errorMessage =
        result.error.issues[0]?.message || 'Invalid scale rating.';
      setError(metricId, errorMessage);
      return;
    }

    const validRating = result.data;

    try {
      // 2. Write validated log to Dexie IndexedDB with UUID and millisecond timestamp
      const today = new Date().toISOString().split('T')[0];
      await db.metricLogs.add({
        id: `log_${crypto.randomUUID()}`,
        metricId: metricId,
        value: validRating,
        timestamp: Date.now(),
      });

      // 3. Clear transient state on success
      clearDraft(metricId);
      onSaveSuccess?.(validRating);
    } catch (err) {
      console.error('Failed to commit scale log to IndexedDB:', err);
      setError(metricId, 'Database error. Could not save.');
    }
  };

  return (
    <Card
      size="2"
      style={{
        backgroundColor: 'var(--sky-dawn, #F9F6F0)',
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <form onSubmit={handleFormSubmit}>
        <Flex direction="column" gap="3">
          {/* Header Row */}
          <Flex justify="between" align="center">
            <Text
              weight="bold"
              size="2"
              style={{ color: 'var(--liturgy-ink)' }}
            >
              {name}
            </Text>
            <Text size="2" weight="bold" color="orange">
              {draftValue} {unit ? `/ 5 ${unit}` : '/ 5'}
            </Text>
          </Flex>

          {/* Slider & Action Row */}
          <Flex gap="3" align="center">
            <Box style={{ flexGrow: 1, padding: '0 4px' }}>
              <Slider
                value={[draftValue]}
                min={1}
                max={5}
                step={1}
                // Radix Slider returns an array; we grab the first element for Zustand
                onValueChange={(val) => setDraftValue(metricId, String(val[0]))}
                color="orange" // Maps visually toward your --aurora-rose brand palette
                size="2"
              />
            </Box>

            <Button type="submit" size="2" variant="soft">
              <CheckCircledIcon width={16} height={16} />
              Save
            </Button>
          </Flex>

          {/* Scale Anchors Feedback */}
          <Flex justify="between" style={{ padding: '0 2px' }}>
            <Text size="1" color="gray">
              1 (Min)
            </Text>
            <Text size="1" color="gray">
              3 (Neutral)
            </Text>
            <Text size="1" color="gray">
              5 (Max)
            </Text>
          </Flex>

          {/* Inline Error Feedback */}
          {error && (
            <Text size="1" color="red" weight="medium">
              {error}
            </Text>
          )}
        </Flex>
      </form>
    </Card>
  );
}
